'use client';

import { useBioUserAccess } from '@ps/features/bio/contexts/user-access/useBioUserAccess';
import { Lock } from 'lucide-react';

import { useCoursePurchased } from '../contexts/course-purchase/context';
import { isClipLockedForViewer } from '../functions/seriesClipAccess';

/**
 * The clip thumbnail's lock visuals, made VIEWER-AWARE.
 *
 * `SeriesClipCellView` and its whole My Life render chain are server components
 * (RSC), so they can't read the viewer's subscription standing — that lives in
 * the client-only `BioUserAccessProvider`. The baked `showUnlockOverlay`
 * (`element.showLockedVideoThumbnail`) is therefore computed with NO viewer
 * context, so it shows the lock to everyone including entitled subscribers.
 *
 * This CLIENT leaf hydrates inside the server-rendered cell, reads the viewer's
 * standing, and only paints the lock when the clip is actually locked FOR THIS
 * VIEWER — a subscribed / following viewer whose tier grants the clip sees the
 * clean thumbnail. Renders the same two visuals the cell used inline: the full
 * blurred "Unlock" overlay (when the thumbnail was server-flagged locked) or the
 * small corner lock cue (a merely access-gated clip).
 */
export function ClipLockOverlay({
  accessLevel,
  showLockedThumbnail,
  courseListingId,
}: {
  accessLevel: string | undefined;
  /** The baked, viewer-agnostic `showUnlockOverlay` VM flag
   *  (`element.showLockedVideoThumbnail`). */
  showLockedThumbnail: boolean;
  /**
   * The derived `listingId` of the clip's parent COURSE, when it is a
   * standalone-purchasable course — so a viewer who bought the course sees no
   * lock on its clips, on the course page AND in off-course bio rails. Absent
   * for plain (non-course) series clips.
   */
  courseListingId?: string;
}) {
  const { currentUserId, creatorId, extendedSubscriptionLevel } =
    useBioUserAccess();
  // A course purchase unlocks every clip in the course (like the owner) —
  // resolved by `listingId` from the bio-wide `CoursePurchaseProvider`; a clip
  // with no parent-course listingId (plain series) reads as not-owned.
  const { purchased: coursePurchased } = useCoursePurchased(courseListingId);

  // Owner viewing their own content bypasses the gate (a creator never
  // subscribes to themselves, so their standing reads as a stranger). Mirrors
  // the player gate's `isOwner` rule.
  const isOwner = currentUserId != null && currentUserId === creatorId;

  // Owner, course-purchaser, or an entitled viewer (subscribed / following at or
  // above the clip's tier, or the clip is ungated) → no lock at all.
  if (
    isOwner ||
    coursePurchased ||
    !isClipLockedForViewer(accessLevel, extendedSubscriptionLevel)
  ) {
    return null;
  }

  if (showLockedThumbnail) {
    return (
      <div
        // z-[4]: below the cell's gradient (z-5) / title (z-6) /
        // chapter-name gutter (z-6) so a locked clip keeps its
        // title + chapter info readable over the blur (QA).
        className="absolute inset-0 z-[4] flex items-center justify-center rounded-t-lg backdrop-blur-[30px]"
        style={{ backgroundColor: 'rgba(72, 72, 72, 0.6)' }}
      >
        <div className="text-content-1 flex items-center justify-center gap-1 rounded border border-white px-2 py-1">
          <Lock className="h-3 w-3" aria-hidden />
          <span className="text-[12px] font-semibold">Unlock</span>
        </div>
      </div>
    );
  }

  // The small bottom-right corner padlock (merely access-gated clips) is
  // intentionally NOT rendered — per request, only the full "Unlock" overlay
  // above marks a locked clip; the tiny lock cue overlapped the title (QA).
  return null;
}
